import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useApp } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { HeartPulse, ArrowLeft, Eye, EyeOff, ChevronDown, ChevronUp, FlaskConical } from "lucide-react";
import { toast } from "sonner";
import LangSwitcher from "@/components/LangSwitcher";

export default function Login() {
  const { login, t } = useApp();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showDemo, setShowDemo] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await login(email, password);
      toast.success("Connexion réussie");
      if (data.user.role === "super_admin") navigate("/super-admin");
      else if (data.user.role === "ministry") navigate("/ministry");
      else navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || t.auth.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] grid lg:grid-cols-2">
      <div className="hidden lg:block relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1665231795856-769fb08a90bc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjbGluaWMlMjBpbnRlcmlvciUyMHdhcm18ZW58MHx8fHwxNzc4MzIyMzAyfDA&ixlib=rb-4.1.0&q=85"
          alt="Clinic" className="w-full h-full object-cover"/>
        <div className="absolute inset-0 bg-gradient-to-tr from-[#1E4631]/85 via-[#1E4631]/40 to-transparent"/>
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-10 h-10 rounded-xl bg-white grid place-items-center">
              <HeartPulse className="w-5 h-5 text-[#1E4631]"/>
            </div>
            <span className="font-[Outfit] font-bold text-2xl">KWIKILA Care</span>
          </div>
          <h2 className="font-[Outfit] text-3xl font-bold leading-tight max-w-md">Connecter chaque patient à des soins de qualité, en RDC.</h2>
        </div>
      </div>
      <div className="flex flex-col">
        <div className="flex items-center justify-between p-6">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#5C6B61] hover:text-[#1E4631]" data-testid="back-home">
            <ArrowLeft className="w-4 h-4"/> Accueil
          </Link>
          <LangSwitcher/>
        </div>
        <div className="flex-1 grid place-items-center px-6 pb-12">
          <form onSubmit={submit} className="w-full max-w-md space-y-6" data-testid="login-form">
            <div>
              <h1 className="text-3xl font-[Outfit] font-bold">{t.auth.title}</h1>
              <div className="mt-3">
                <button
                  type="button"
                  onClick={() => setShowDemo(v => !v)}
                  className="inline-flex items-center gap-1.5 text-xs text-[#5C6B61] hover:text-[#1E4631] px-2.5 py-1.5 rounded-md border border-dashed border-[#E5EBE7] hover:border-[#1E4631]/40 transition"
                  data-testid="toggle-demo-creds"
                >
                  <FlaskConical className="w-3.5 h-3.5"/>
                  Comptes démo (testeurs)
                  {showDemo ? <ChevronUp className="w-3.5 h-3.5"/> : <ChevronDown className="w-3.5 h-3.5"/>}
                </button>
                {showDemo && (
                  <div className="mt-2 p-3 bg-[#1E4631]/5 rounded-lg text-xs text-[#5C6B61] space-y-1 border border-[#1E4631]/10" data-testid="demo-creds-panel">
                    <div className="font-semibold text-[#1E4631]">Comptes de démo :</div>
                    <div>Super-Admin: <code>admin@kwikila.cd</code> / admin123</div>
                    <div>Admin hôpital: <code>admin@demo-clinic.cd</code> / demo123</div>
                    <div>Médecin: <code>doctor@demo-clinic.cd</code> / demo123</div>
                    <div>Infirmier(e): <code>nurse@demo-clinic.cd</code> / demo123</div>
                    <div>Secrétariat: <code>secretary@demo-clinic.cd</code> / demo123</div>
                  </div>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">{t.auth.email}</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email"/>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">{t.auth.password}</Label>
                <Link to="/forgot-password" className="text-xs text-[#D96C4A] hover:underline font-medium" data-testid="link-forgot">
                  Mot de passe oublié ?
                </Link>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="pr-10"
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  aria-label={showPw ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 rounded text-[#5C6B61] hover:text-[#1E4631] hover:bg-[#1E4631]/5 transition"
                  data-testid="toggle-password"
                >
                  {showPw ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}
                </button>
              </div>
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-[#1E4631] hover:bg-[#163424] text-white py-6 rounded-lg font-semibold" data-testid="login-submit">
              {loading ? "..." : t.auth.submit}
            </Button>
            <div className="text-center text-sm text-[#5C6B61]">
              Pas encore inscrit ? <Link to="/register-hospital" className="text-[#D96C4A] font-semibold hover:underline" data-testid="link-register">Inscrire mon hôpital</Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
